import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  WarningAmberOutlined as WarningIcon,
  CheckCircleOutlined as CheckIcon,
} from '@mui/icons-material';

const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Delete',
  entityName,
  entityType = 'item',
  impacts = [],
  loading = false,
}) => {
  const getDefaultImpacts = () => {
    switch (entityType) {
      case 'director':
        return [
          'Remove director from this company',
          'Keep director in the system pool',
          'Retain all historical records',
          'Remove committee memberships',
        ];
      case 'committee':
        return [
          'Remove all committee members',
          'Archive all committee meetings',
          'Retain historical data',
          'Cannot be undone',
        ];
      case 'document':
        return [
          'Permanently delete this document',
          'Remove from all records',
          'Cannot be recovered',
        ];
      case 'company':
        return [
          'Remove company from system',
          'Unassign all directors',
          'Archive all related data',
          'Cannot be undone',
        ];
      default:
        return ['This item will be permanently deleted'];
    }
  };

  const displayImpacts = impacts.length > 0 ? impacts : getDefaultImpacts();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'grey.500' }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete{entityName ? ':' : ' this item?'}
          </Typography>
          
          {entityName && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body1" fontWeight={600}>
                {entityName}
              </Typography>
              {entityType && (
                <Typography variant="caption" color="text.secondary">
                  Type: {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            This action will:
          </Typography>
        </Alert>

        <List dense>
          {displayImpacts.map((impact, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText
                primary={impact}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary',
                }}
              />
            </ListItem>
          ))}
        </List>

        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            This cannot be undone.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Yes, Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
